import { ChangeEvent, ReactNode, SyntheticEvent, useContext, useEffect, useState } from "react"
import { InputText } from "primereact/inputtext"
import FieldError, { FormErrors } from "../../FieldError"
import { GetUserResponseData, LabelFormat, UpdateUserPayload } from "../../../api/types/user"
import { validateUser } from "./validate"
import { Checkbox, CheckboxChangeParams } from "primereact/checkbox"
import { classNames } from "primereact/utils"
import commonStyles from "../../common.module.css"
import { Dropdown, DropdownChangeParams, DropdownProps } from "primereact/dropdown"
import { useUserDetail } from "../../../api/queries/useUserDetail"
import { useNavigate, useParams } from "react-router-dom"
import { Address } from "../../../api/types/address"
import { Button } from "primereact/button"
import { useCreateUser } from "../../../api/queries/useCreateUser"
import { useUpdateUser } from "../../../api/queries/useUpdateUser"
import { useDeleteUser } from "../../../api/queries/useDeleteUser"
import { useAddresses } from "../../../api/queries/useAddresses"
import styles from "../styles.module.css"
import userContext from "../../../context/user"
import { PhoneInput } from "../../common/PhoneInput"
import { splitNumber } from "../../common/PhoneInput/utils"
import { formatAddress } from "../../../common/formatAddress"
import { scrollToFirstInvalidElement } from "../../../common/util"
import { useConstants } from "../../../api/queries/useConstants"

type Params = "uuid"

export default function UserForm() {
  const navigate = useNavigate()
  const { uuid } = useParams<Params>()
  const { user } = useContext(userContext)
  const createUser = useCreateUser()
  const updateUser = useUpdateUser(uuid)
  const deleteUser = useDeleteUser(uuid)
  const { data: userDetail } = useUserDetail(uuid)
  const { data: addresses } = useAddresses({ limit: 0, offset: 0 })
  const { data: constants } = useConstants()
  const labelFormatOptions = [
    { value: "A4", label: "A4 - 4 štítky na stránku" },
    { value: "A6", label: "A6 - 1 štítek na stránku" }
  ]

  const [firstname, setFirstname] = useState<string>("")
  const handleFirstnameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setFirstname(event.target.value)
  }

  const [lastname, setLastname] = useState<string>("")
  const handleLastnameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setLastname(event.target.value)
  }

  const [email, setEmail] = useState<string>("")
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value)
  }

  const [billingEmail, setBillingEmail] = useState<string>("")
  const handleBillingEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setBillingEmail(event.target.value)
  }

  const [phonePrefix, setPhonePrefix] = useState<string>("")
  const [phone, setPhone] = useState<number | null>(null)
  const handlePhoneChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setPhonePrefix(prefix)
    setPhone(value)
    resetErrorsFor(["phone", "mobile"])
  }

  const [mobilePrefix, setMobilePrefix] = useState<string>("")
  const [mobile, setMobile] = useState<number | null>(null)
  const handleMobileChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setMobilePrefix(prefix)
    setMobile(value)
    resetErrorsFor(["mobile", "phone"])
  }

  const [canManageUsers, setCanManageUsers] = useState<boolean>(false)
  const handleCanManageUsersChange = (event: CheckboxChangeParams): void => {
    setCanManageUsers(event.checked)
  }

  const [billingAddress, setBillingAdress] = useState<string>("")
  const handleBillingAddressChange = (event: DropdownChangeParams): void => {
    setBillingAdress(event.value)
  }

  const [labelFormat, setLabelFormat] = useState<LabelFormat>("A6")
  const handleLabelFormatChange = (event: DropdownChangeParams): void => {
    setLabelFormat(event.value)
  }

  useEffect(() => {
    if (userDetail && constants) {
      const res = userDetail.results.userDetail

      setFirstname(res.firstname)
      setLastname(res.lastname)
      setEmail(res.email)
      setBillingEmail(res.billingEmail)
      setCanManageUsers(res.canManageUsers)
      setBillingAdress(res.billingAddress || "")
      setLabelFormat(res.labelFormat || "A6")

      if (res.phone) {
        const { prefix, number } = splitNumber(constants.results.countries, res.phone)
        setPhonePrefix(prefix)
        setPhone(number)
      }

      if (res.mobile) {
        const { prefix, number } = splitNumber(constants.results.countries, res.mobile)
        setMobilePrefix(prefix)
        setMobile(number)
      }
    }
  }, [userDetail, constants])

  const handleDeleteUserClick = async () => {
    await deleteUser.mutateAsync()

    navigate("/users")
  }

  const [errors, setErrors] = useState<FormErrors<UpdateUserPayload>>()

  const handleFormSubmit = async (event: SyntheticEvent) => {
    event.preventDefault()

    const payload: UpdateUserPayload = {
      firstname,
      lastname,
      email,
      billingEmail,
      phone: phone ? `${phonePrefix}${phone}` : "",
      mobile: mobile ? `${mobilePrefix}${mobile}` : "",
      canManageUsers,
      billingAddress,
      // let user change default loading address in their profile
      loadingDefaultAddress: uuid && userDetail ? userDetail.results.userDetail.loadingDefaultAddress : null,
      labelFormat,
    }

    const [isValid, errors] = validateUser(payload)
    setErrors(errors)

    if (isValid) {
      if (uuid) {
        await updateUser.mutateAsync(payload)
          .then(() => navigate("/users"))
          .catch(({ response }) => {
            let { data }: { data: GetUserResponseData } = response
            let respErrors: FormErrors<UpdateUserPayload> = {}

            data.errors.forEach((error) => {
              if (typeof error.property === "string") {
                respErrors[error.property as keyof UpdateUserPayload] = error.message
              }
            })

            setErrors(respErrors)
            scrollToFirstInvalidElement()
          })
      } else {
        await createUser.mutateAsync(payload)
          .then(() => navigate("/users"))
          .catch(({ response }) => {
            let { data }: { data: GetUserResponseData } = response
            let respErrors: FormErrors<UpdateUserPayload> = {}

            data.errors.forEach((error) => {
              if (typeof error.property === "string") {
                respErrors[error.property as keyof UpdateUserPayload] = error.message
              }
            })

            setErrors(respErrors)
            scrollToFirstInvalidElement()
          })
      }
    }
  }

  const tAddressItem = (option: Address): ReactNode => formatAddress(option)

  const tAddressValue = (option: Address, { placeholder }: DropdownProps): ReactNode => {
    if (option) {
      return formatAddress(option)
    }

    return <div className={styles.placeholder}>{placeholder}</div>
  }

  const resetErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}

    names.forEach((name) => { obj[name] = "" })

    setErrors(Object.assign({}, errors, obj))
  }

  const isAdmin: boolean = user?.isAdmin || false
  const isSelf: boolean = uuid === user?.uuid || false
  const isCanManageUsersDisabled: boolean = !isAdmin || isSelf

  return (
    <form className={"col-12"} onSubmit={handleFormSubmit}>
      <div className={"p-fluid"}>
        <div className={"block-title block-title_blue px-3 py-2 border-round-top flex justify-content-between h-auto align-items-center"}>
          {uuid ? (
            <>
              <span>
                {user?.isActive
                  ? <i className={"pi pi-check-circle text-green-700"} title={"registrace potvrzena"}></i>
                  : <i className={"pi pi-exclamation-circle text-red-700"} title={"registrace čeká na potvrzení"}></i>
                }
                <span className={"ml-3"}>Uživatel</span>
              </span>

              {!isSelf && (
                <Button type={"button"} label={"Deaktivovat"}
                  className={"p-button p-button-sm p-button-danger w-auto p-button-responsive"}
                  icon={"pi pi-trash"}
                  disabled={updateUser.isLoading} onClick={handleDeleteUserClick} />
              )}
            </>
          ) : (
            "Uživatel"
          )}
        </div>

        <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={firstname} onChange={handleFirstnameChange}
                  className={classNames({ "p-invalid": !!errors?.firstname })} />
                <label className={commonStyles.labelRequired}>Jméno:</label>
              </span>
              <FieldError error={errors?.firstname} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={lastname} onChange={handleLastnameChange}
                  className={classNames({ "p-invalid": !!errors?.lastname })} />
                <label className={commonStyles.labelRequired}>Příjmení:</label>
              </span>
              <FieldError error={errors?.lastname} />
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={email} onChange={handleEmailChange}
                  className={classNames({ "p-invalid": !!errors?.email })}
                  aria-autocomplete={"none"} />
                <label className={commonStyles.labelRequired}>E-mail:</label>
              </span>
              <FieldError error={errors?.email} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={billingEmail} onChange={handleBillingEmailChange}
                  className={classNames({ "p-invalid": !!errors?.billingEmail })}
                  aria-autocomplete={"none"} />
                <label>E-mail pro fakturaci:</label>
              </span>
              <FieldError error={errors?.billingEmail} />
              <small className={commonStyles.fieldHelp}>Tato adresa bude použita pro zasílání faktur, jinak se použije fakturační e-mail zadaný u admina společnosti.</small>
              <small className={commonStyles.fieldHelp}> Více e-mailů oddělte středníkem.</small>
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <PhoneInput placeholder={"Mobil:"} prefix={mobilePrefix}
                value={mobile} onChange={handleMobileChange}
                isRequired={!phone} error={errors?.mobile} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <PhoneInput placeholder={"Telefon:"} prefix={phonePrefix}
                value={phone} onChange={handlePhoneChange}
                isRequired={!mobile} error={errors?.phone} />
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-8"}>
              <span className="p-float-label">
                <Dropdown options={addresses?.results.addressList || []}
                  optionValue={"uuid"} optionLabel={"name"}
                  itemTemplate={tAddressItem} valueTemplate={tAddressValue}
                  value={billingAddress} onChange={handleBillingAddressChange}
                  className={classNames({ "p-invalid": !!errors?.billingAddress })}
                  placeholder={""} />
                <label className={commonStyles.labelRequired}>Fakturační adresa:</label>
              </span>
              <FieldError error={errors?.billingAddress} />
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <Dropdown options={labelFormatOptions} optionValue={"value"} optionLabel={"label"}
                  value={labelFormat} onChange={handleLabelFormatChange}
                  className={classNames({ "p-invalid": !!errors?.labelFormat })} />
                <label className={commonStyles.labelRequired}>Formát štítku:</label>
              </span>
              <FieldError error={errors?.labelFormat} />
            </div>
          </div>

          <div className={"grid"}>
            <div className={" col-12 md:col-4 pb-3"}>
              <div className={"field-checkbox"}>
                <Checkbox checked={canManageUsers} onChange={handleCanManageUsersChange}
                  name={"can-manage-users"} inputId={"can-manage-users"}
                  disabled={isCanManageUsersDisabled}
                  className={classNames({ "p-invalid": !!errors?.canManageUsers })} />
                <label htmlFor={"can-manage-users"}>může spravovat uživatele</label>
              </div>
              <FieldError error={errors?.labelFormat} />
            </div>
          </div>
        </div>
      </div>

      <div className={"text-right"}>
        <Button type={"submit"} label={uuid ? "Aktualizovat" : "Vytvořit"}
          className={"p-button p-button-sm p-button-success"}
          icon={"pi pi-save"}
          disabled={createUser.isLoading || updateUser.isLoading} />
      </div>
    </form>
  )
}
